// React
import React, { useState, useEffect, ReactElement, useRef } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Modules
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CustomCheckbox } from '@reach/checkbox';
import ReCAPTCHA from 'react-google-recaptcha';
// Design system
import { Font } from 'src/design-system/text/font';
import { Input } from 'src/design-system/input/Input';
import { Textarea } from 'src/design-system/textarea/Textarea';
import { Button } from 'src/design-system/button/Button';
import { Select } from 'src/design-system/select/Select';
import { InputMessage } from 'src/design-system/input-message/InputMessage';
import { Link } from '@utmb/design-system/link/link';
// import { InputFile } from 'src/design-system/input-file/input-file';
// Contexts
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Types
import { Slice } from '@utmb/types/slice-handler';
import ContactTopicView from '@utmb/types/view/ContactTopicView';
import SliceContactFormView from '@utmb/types/view/SliceContactFormView';
// Utils
import { v4 as uuidv4 } from 'uuid';
import { ListboxOption } from '@reach/listbox';
import { TENANCY_HTTP_HEADER } from '@utmb/utils/utmb';
// Styles
import styles from './contact-form.module.scss';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';

export const ContactForm = ({ id, topics, privacyLink }: Slice<SliceContactFormView>): ReactElement => {
    const { t } = useTranslation();

    const { tenantId } = useTenancy();

    const noInputError = t('contactForm.noInputError');

    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const [errorCaptchaVisible, setErrorCaptchaVisible] = useState(false);
    const [captchaErrorMessageText, setCaptchaErrorMessageText] = useState('');
    const [errorMessageVisible, setErrorMessageVisible] = useState(false);
    const [successMessageVisible, setSuccessMessageVisible] = useState(false);
    const [apiSuccessMessage, setApiSuccessMessage] = useState('');
    const [apiErrorMessageText, setApiErrorMessageText] = useState('');

    // const [file, setfile] = useState<FileList | null>(null);

    const [topic, setTopic] = useState('');

    let successTimer: ReturnType<typeof setTimeout>;
    let errorTimer: ReturnType<typeof setTimeout>;

    const initializeFormCustom = (): void => {
        setTopic('');
    };

    const formik = useFormik({
        initialValues: {
            fullName: '',
            email: '',
            message: '',
            checked: [] as string[],
        },
        validationSchema: Yup.object({
            email: Yup.string().email(t('contactForm.mailFormatError')).required(noInputError),
            fullName: Yup.string().required(noInputError),
            message: Yup.string().required(noInputError),
            checked: Yup.array().min(1, t('contactForm.errorPolicy')),
        }),
        onSubmit: (values, { resetForm }) => {
            const token = recaptchaRef?.current?.getValue();

            if (!token || token === '') {
                setErrorCaptchaVisible(true);
                setCaptchaErrorMessageText(t('error.recaptcha'));
            } else {
                setErrorCaptchaVisible(false);

                if (topic) {
                    fetch(`${process.env.NEXT_PUBLIC_FRONTEND_API_URL}/content/contact`, {
                        headers: { 'content-type': 'application/json', [TENANCY_HTTP_HEADER]: tenantId },
                        body: JSON.stringify({
                            fullName: values.fullName,
                            email: values.email,
                            topic: topic,
                            message: values.message,
                            reCaptchaToken: String(token),
                        }),
                        method: 'POST',
                    })
                        // .then((response) => response.json()) // if use response body status and message fix status 200...
                        .then((res) => {
                            if (res.status === 201) {
                                setSuccessMessageVisible(true);
                                setApiSuccessMessage(t('contactForm.sendSucces'));
                                successTimer = setTimeout(() => {
                                    setSuccessMessageVisible(false);
                                }, 10000);
                                resetForm({
                                    values: {
                                        fullName: '',
                                        email: '',
                                        message: '',
                                        checked: [],
                                    },
                                });
                                initializeFormCustom();
                            } else {
                                setErrorMessageVisible(true);
                                setApiErrorMessageText(t('contactForm.sendError'));
                                errorTimer = setTimeout(() => {
                                    setErrorMessageVisible(false);
                                }, 10000);
                            }
                        })
                        .catch((error) => {
                            console.error('error', error);
                            setApiErrorMessageText(error.message);
                            setErrorMessageVisible(true);
                            errorTimer = setTimeout(() => {
                                setErrorMessageVisible(false);
                            }, 10000);
                        });
                } else {
                    setErrorMessageVisible(true);
                    setApiErrorMessageText(t('contactForm.topicError'));
                    errorTimer = setTimeout(() => {
                        setErrorMessageVisible(false);
                    }, 10000);
                }
            }
        },
    });

    /**
     * On component unmount, safelly clear the timers
     */
    useEffect(() => {
        return () => {
            clearTimeout(successTimer);
            clearTimeout(errorTimer);
        };
    });

    return (
        <div id={id} className={styles.container} style={{ marginBottom: '100px' }}>
            <form onSubmit={formik.handleSubmit} className={styles.form}>
                <div className="container">
                    <div className="row">
                        <div className="mt-5 col-lg-6">
                            <Input
                                label={t('contactForm.labelFullName')}
                                id="fullName"
                                name="fullName"
                                type="text"
                                className={styles.input}
                                placeholder={t('contactForm.placeholderFullName')}
                                onchange={formik.handleChange}
                                onblur={formik.handleBlur}
                                value={formik.values.fullName}
                                require={true}
                            />
                            <div>
                                {formik.touched.fullName && formik.errors.fullName && (
                                    <InputMessage type="error" message={formik.errors.fullName}></InputMessage>
                                )}
                            </div>
                        </div>
                        <div className="mt-5 col-lg-6">
                            <Input
                                label={t('contactForm.labelEmail')}
                                id="email"
                                name="email"
                                type="text"
                                className={styles.input}
                                placeholder={t('contactForm.placeholderEmail')}
                                onchange={formik.handleChange}
                                onblur={formik.handleBlur}
                                value={formik.values.email}
                                autoComplete={true}
                                require={true}
                            />
                            <div>{formik.touched.email && formik.errors.email && <InputMessage type="error" message={formik.errors.email}></InputMessage>}</div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="mt-5">
                        <Select
                            label={t('contactForm.labelTopic')}
                            defaultValue={t('contactForm.defaultValueTopic')}
                            id="topic"
                            name="topic"
                            onchange={(value: string) => {
                                setTopic(value);
                            }}
                            value={topic}
                            require={true}
                        >
                            {topics?.map((item: ContactTopicView) => (
                                <ListboxOption key={uuidv4()} value={item.topic}>
                                    <Font.FuturaBook as="span" mobile="14" desktop="16" className={styles.label}>
                                        {item.label}
                                    </Font.FuturaBook>
                                </ListboxOption>
                            ))}
                        </Select>
                    </div>
                </div>
                <div className="container">
                    <div className="mt-5">
                        <Textarea
                            label={t('contactForm.labelMessage')}
                            id="message"
                            name="message"
                            className=""
                            placeholder={t('contactForm.placeholderMessage')}
                            onchange={formik.handleChange}
                            onblur={formik.handleBlur}
                            value={formik.values.message}
                            require={true}
                        />
                        <div>
                            {formik.touched.message && formik.errors.message && <InputMessage type="error" message={formik.errors.message}></InputMessage>}
                        </div>
                    </div>
                </div>
                {/* <div className="container">
                    <div className="mt-5">
                        <InputFile
                            id="file"
                            name="file"
                            onchange={(event: ChangeEvent<HTMLInputElement>) => {
                                setfile(event.target.files);
                            }}
                            accept="image/png, image/jpeg, doc,.docx, .pdf" // and more...
                        />
                    </div>
                </div> */}
                <div className={'container'}>
                    <label className={styles.privacy_policy}>
                        <CustomCheckbox name="checked" value="policy" checked={!!~formik.values.checked.indexOf('policy')} onChange={formik.handleChange} />
                        <Font.FuturaMedium as="span" mobile="14" desktop="18" className={`${styles.label}`}>
                            {t('contactForm.iAgreeTothe')}{' '}
                            {privacyLink && (
                                <Link.Default
                                    href={privacyLink.slug}
                                    followClassName={`${privacyLink.followClass}-ga`}
                                    label={privacyLink.label}
                                    className={styles.link}
                                />
                            )}{' '}
                            {!privacyLink && <span className={styles.important}>{t('contactForm.privacyPolicy')}</span>}
                        </Font.FuturaMedium>
                    </label>
                </div>

                <div className={'container'}>
                    <div>
                        {formik.touched.checked && formik.errors.checked && (
                            <InputMessage type="error" message={formik.errors.checked as string}></InputMessage>
                        )}
                    </div>

                    {/* Status Messages Variations */}
                    <div>{errorMessageVisible && <InputMessage type="error" message={apiErrorMessageText} className="error-hide"></InputMessage>}</div>
                    <div>{successMessageVisible && <InputMessage type="success" message={apiSuccessMessage}></InputMessage>}</div>
                </div>

                {process.env.NEXT_PUBLIC_RECAPTCHA_SITE && (
                    <div className={styles.captcha}>
                        <div className={styles.error_holder}>
                            {errorCaptchaVisible && <InputMessage type="error" message={captchaErrorMessageText} className="error" />}
                        </div>
                        <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE} />
                    </div>
                )}

                <Button.Default
                    className={styles.form_button}
                    type="submit"
                    label={t('contactForm.SendToTheSupport')}
                    disabled={formik.isSubmitting}
                    bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                ></Button.Default>
            </form>
        </div>
    );
};
